import React, { ReactElement, SyntheticEvent, useState } from 'react';
import Grid from "@mui/material/Grid";
import { TextField, Typography } from "@mui/material";
import { LoadingButton } from "../components/LoadingButton";
import { useAlert } from "../contexts/alert/useAlert";
import { ConflictError, HttpClient, NotFoundError } from "../api/HttpClient";
import { Action, Category } from "../analytics/Tracking";
import { InputValidatorMode, useUserNameValidator } from "../contexts/useInputValidator";
import { usePlayer } from '../contexts/usePlayer';

import { useTracking } from '../analytics/useTracking';
import { SessionStorage } from '../localstorage/SessionStorage';



export interface RenamePlayerProps {
	roomUrlId: string;
	onCompleted: (newPlayerName: string) => void;
}

export function RenamePlayer(props: RenamePlayerProps): ReactElement {
	const alert = useAlert();
	const sessionStorage = new SessionStorage();
	const { track } = useTracking();
	const [isLoading, setIsLoading] = useState(false);

	const {player, updatePlayer }= usePlayer();
	const [newPlayerName, setNewPlayerName] = useState(player?.name);
	const [validateUserName] = useUserNameValidator();
	const [isValid, setIsValid] = useState(true);

	function onCompleted(newPlayerName: string) {
		props.onCompleted(newPlayerName);
	}

	function validateInput(inputUserName: string, mode: InputValidatorMode) {
		const isValid = validateUserName(inputUserName, mode).isValid;
		setIsValid(isValid);
		return isValid;
	}

	async function handleRenamePlayer(event: SyntheticEvent) {
		event.preventDefault();
	
		if (!validateInput(newPlayerName as string, InputValidatorMode.onSubmit)) {
			return;
		}

		setIsLoading(true);
		const httpClient = new HttpClient();

		try {
			(await httpClient.post("/rooms/renamePlayer", { roomUrlId: props.roomUrlId, playerId: player?.id, newPlayerName: newPlayerName }));

			setIsLoading(false);

			track.event(Category.Room, Action.PlayerRenamed);
			if (player && newPlayerName) {
				sessionStorage.UpdatePlayerName(props.roomUrlId, newPlayerName);
				updatePlayer(newPlayerName, player.isModerator);
				onCompleted(newPlayerName);
			}
			
		} catch (e) {
			let errorMessage = "Could not rename player, please try again 🤞";

			// The two known outcomes are validation the user is asked to correct, so they warn and
			// stay out of elmah.io (which captures console.error). Anything else is unexpected and
			// must report, otherwise a failing rename is invisible behind the generic message.
			if (e instanceof ConflictError) {
				console.warn("HandleRenamePlayer - Player name already exists.");
				errorMessage = "Player name already in use, please try another 👍";
			} else if (e instanceof NotFoundError) {
				console.warn("HandleRenamePlayer - Room not found.");
				errorMessage = "Player not found in room 🤞";
			} else {
				console.error("HandleRenamePlayer failed:", e);
			}
			setIsLoading(false);
			alert.showMessage(errorMessage, "error");
		} 
	}

	function handleOnChange(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
		setNewPlayerName(event.target.value);
		validateInput(event.target.value, InputValidatorMode.onTyping);
	}

	return (
		<>
			<form onSubmit={handleRenamePlayer}>
				<Grid container sx={{ padding: 3, marginBottom: 3 }} spacing={3} direction="column" justifyContent="center" alignItems="center">
					<Grid>
						<Typography align="center" variant="h5">Rename Player</Typography>
						<Typography align="center" variant="subtitle1">and change your identity 🤖</Typography>
					</Grid>
					<Grid>
						<TextField
							autoFocus={true}
							inputProps={{
								onFocus: (event: React.FocusEvent<HTMLInputElement>) => {
									setTimeout(() => event.target.select(), 0);
								}
							}}
							value={newPlayerName}
							error={!isValid}
							helperText={validateUserName(newPlayerName as string, InputValidatorMode.onTyping).errorMessage}
							disabled={isLoading}
							name="newPlayerName"
							id="outlined-basic"
							label="Enter new Player Name"
							variant="outlined"
							color="secondary"
							onChange={handleOnChange}
						/>
{/*							onChange={(event) => handleOnChangeNewName(event)}*/}
					</Grid>
					<Grid>
						<LoadingButton type="submit" isLoading={isLoading}>Rename</LoadingButton>
					</Grid>
				</Grid>
			</form>
		</>
	);
}